/**
 * AssetLibraryGrid — Session-Gruppe + Draggable-Thumbnail des AssetLibraryPanel.
 * Aus AssetLibraryPanel extrahiert (Step-0-Split) und an Mock #10 angeglichen:
 * theme-aware (folgt App-Theme statt fest dunkel) + Dateiname-Label unter jedem
 * Thumbnail. Drag-Data (`library-<assetId>`, fromLibrary) bleibt unverändert.
 */

import { ChevronDown } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { ProductImageAsset } from '@/types/image-collection';
import { cn } from '@/lib/utils';

export function SessionGroup({
  dateLabel,
  timeLabel,
  sessionLabel,
  assets,
  isExpanded,
  onToggle,
  productId,
  onAdd,
}: {
  sessionKey?: string;
  dateLabel: string;
  timeLabel: string;
  sessionLabel: string;
  assets: ProductImageAsset[];
  isExpanded: boolean;
  onToggle: () => void;
  productId: string;
  onAdd?: (asset: ProductImageAsset) => void;
}) {
  return (
    <div className={cn(!isExpanded && 'opacity-60 hover:opacity-100 transition-opacity')}>
      <button onClick={onToggle} className="flex items-center gap-2 w-full text-left mb-3 group">
        <ChevronDown
          className={cn(
            'w-4 h-4 text-neutral-400 dark:text-neutral-500 group-hover:text-primary-500 transition-all',
            !isExpanded && '-rotate-90',
          )}
        />
        <div className="flex-1">
          <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            {dateLabel} {timeLabel && dateLabel !== 'Gestern' ? timeLabel : ''}
          </span>
          <p className={cn('text-sm font-medium', isExpanded ? 'text-neutral-900 dark:text-white' : 'text-neutral-500 dark:text-neutral-400')}>
            {sessionLabel}
          </p>
        </div>
        <span className="text-sm text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
          {assets.length}
        </span>
      </button>

      {isExpanded && (
        <div className="grid grid-cols-2 gap-3">
          {assets.map((asset) => (
            <DraggableAssetThumbnail key={asset.asset_id} asset={asset} productId={productId} onAdd={onAdd} />
          ))}
        </div>
      )}
    </div>
  );
}

export function DraggableAssetThumbnail({ asset, productId: _productId, onAdd }: { asset: ProductImageAsset; productId: string; onAdd?: (asset: ProductImageAsset) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `library-${asset.asset_id}`,
    data: {
      type: 'library-asset',
      asset: {
        asset_id: asset.asset_id,
        link_id: `library-${asset.asset_id}`,
        channel_id: null,
        channel_name: 'Library',
        channel_type: null,
        slot: 0,
        sort_number: 0,
        alt_text: asset.alt_text,
        s3_key_original: null,
        s3_url: asset.s3_url,
        width: asset.width,
        height: asset.height,
        mime_type: null,
        content_hash: null,
        upload_source: asset.upload_source,
      },
      channelKey: 'library',
      channelId: null,
      fromLibrary: true,
    },
  });

  const style = { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.5 : 1 };
  const imageUrl = `/api/v1/asset/${asset.asset_id}/image?size=thumbnail`;
  const fileName = asset.alt_text || asset.s3_key?.split('/').pop() || asset.asset_id;

  return (
    <div className="group w-full max-w-[200px] mx-auto">
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={onAdd ? () => onAdd(asset) : undefined}
        className={cn(
          'relative aspect-square rounded-lg border overflow-hidden',
          onAdd ? 'cursor-pointer' : 'cursor-grab',
          'border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800',
          'hover:border-primary-500 hover:scale-[1.02] transition-all',
          isDragging && 'ring-2 ring-primary-500',
        )}
      >
        <img src={imageUrl} alt={asset.alt_text || 'Asset'} className="w-full h-full object-cover" loading="lazy" draggable={false} />
        <span
          className="absolute top-1 right-1 text-sm opacity-70 group-hover:opacity-100 transition-opacity"
          title={getSourceLabel(asset.upload_source)}
        >
          {getSourceEmoji(asset.upload_source)}
        </span>
        {onAdd && (
          <span className="absolute inset-0 flex items-center justify-center bg-primary-600/0 group-hover:bg-primary-600/40 text-white opacity-0 group-hover:opacity-100 transition-all text-2xl font-light">
            +
          </span>
        )}
      </div>
      <p className="mt-1 px-0.5 text-2xs text-neutral-500 dark:text-neutral-400 truncate" title={fileName}>
        {fileName}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers (Quell-/Session-Labels)
// ---------------------------------------------------------------------------

export function getSourceLabel(source: string | null): string {
  switch (source) {
    case 'ai_generated': case 'ai': case 'generated': return 'KI-generiert';
    case 'user_upload': case 'upload': case 'user': return 'Upload';
    case 'jtl_original': case 'integration_sync': case 'jtl': case 'sync': return 'JTL Import';
    case 'optimized': return 'Optimiert';
    default: return 'Unbekannt';
  }
}

export function getSessionLabel(source: string | null): string {
  switch (source) {
    case 'ai_generated': case 'ai': case 'generated': return '✨ KI-Session';
    case 'user_upload': case 'upload': case 'user': return '☁️ Uploads';
    case 'jtl_original': case 'integration_sync': case 'jtl': case 'sync': return '🔄 JTL Import';
    case 'optimized': return '⚡ Optimiert';
    default: return '📷 Assets';
  }
}

export function getSourceEmoji(source: string | null): string {
  switch (source) {
    case 'ai_generated': case 'ai': case 'generated': return '✨';
    case 'user_upload': case 'upload': case 'user': return '☁️';
    case 'jtl_original': case 'integration_sync': case 'jtl': case 'sync': return '🔄';
    case 'optimized': return '⚡';
    default: return '📷';
  }
}

export interface AssetGroup { dateLabel: string; timeLabel: string; sessionLabel: string; assets: ProductImageAsset[] }

/** Gruppiert Assets nach Session/Datum (heute/gestern/Datum). */
export function buildGroups(assets: ProductImageAsset[]): Map<string, AssetGroup> {
  const groups = new Map<string, AssetGroup>();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  for (const asset of assets) {
    const createdAt = new Date(asset.created_at);
    const dateOnly = new Date(createdAt);
    dateOnly.setHours(0, 0, 0, 0);
    const timeLabel = createdAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    let groupKey: string;
    let dateLabel: string;
    if (dateOnly.getTime() === today.getTime()) {
      groupKey = 'today';
      dateLabel = 'Heute';
    } else if (dateOnly.getTime() === yesterday.getTime()) {
      groupKey = 'yesterday';
      dateLabel = 'Gestern';
    } else {
      groupKey = dateOnly.toISOString().split('T')[0];
      dateLabel = dateOnly.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { dateLabel, timeLabel, sessionLabel: getSessionLabel(asset.upload_source), assets: [] });
    }
    groups.get(groupKey)!.assets.push(asset);
  }
  return groups;
}
